/**
 * Golem fork (FT-1200): THE ACCOUNT — the platform login, spoken from the app.
 *
 * The site is served beside the platform with `/api` proxied same-origin
 * (deploy/Caddyfile.botc-snippet in production, vue.config.js's dev proxy in
 * dev), so the platform's auth endpoints are simply here: cookies ride every
 * request, and signing in on the main site IS being signed in here. This
 * module is the only place the app speaks to `/api/auth/*`; every other
 * surface reads the result off the session store (`session.account`).
 *
 * The contract (server/auth/routes.ts, frozen):
 *   GET  /api/auth/me      → 200 { user: PublicUser | null }   (never 401)
 *   POST /api/auth/login   { email, password } → 200 { user } | 401 { message }
 *   POST /api/auth/signup  { email, password (min 8), name? }
 *                          → 201 { user } | 409 "Email already registered"
 *                            (signup can also be refused outright by the
 *                             platform's `createAccount` feature flag — the
 *                             server's own message is what the user sees)
 *   POST /api/auth/logout  → 200 { ok: true }
 *   POST /api/auth/forgot-password { email }
 *                          → 200 { ok: true } ALWAYS (an unknown email gets
 *                            the same answer a known one does — the endpoint
 *                            never reveals who has an account)
 *                          | 429 { message } rate-limited | 400 { message }
 *
 * WHAT IS KEPT, AND WHAT NEVER IS. The store holds `{ id, name, email }` —
 * the opaque account id, the display name, and the email FOR THE PANEL'S OWN
 * "who am I" line only. No password ever touches this module's state, no
 * token is stored (the session is a cookie the browser owns), and nothing
 * account-shaped is written to localStorage. The email never crosses the
 * game's wire — the only identity fact that ever leaves this client is the
 * opaque id, direct to the host (see socket.js's claim path).
 */

// FT-1237: the server's labs verdict routes through the local-override
// switch now, so a tester's own word (the hidden corner, LabsSwitch.vue)
// keeps winning across this fetch resolving.
import { setServerLabs } from "./labsSwitch";

const API = "/api/auth";

/** PublicUser → the app's own slim account fact. `role` (FT-1403) is the
 *  platform's account tier ("user" | "superuser", FT-397) and rides along so
 *  owner-only surfaces (the Night lab's menu row) can key on the SAME tier
 *  the platform's own superuser gates key on. An unknown or missing value is
 *  simply not "superuser" — the strict compare downstream fails closed, the
 *  same degrade-to-least-privilege rule the platform's normalizeUserRole
 *  applies server-side. */
const slim = (user) =>
  user
    ? {
        id: user.id,
        name: user.name || null,
        email: user.email,
        role: user.role || null,
      }
    : null;

/**
 * Read the server's message out of a failed response, falling back to a
 * plain sentence — the server's own words are the error surface here
 * (a 401's "Invalid email or password", the flag refusal, a 409), spoken
 * as-is in the app's panel.
 */
async function messageOf(res, fallback) {
  try {
    const body = await res.json();
    if (body && typeof body.message === "string" && body.message) {
      return body.message;
    }
    // A feature-flag refusal (403 { error: "feature_disabled", feature })
    // carries no `message` — it carries the switched-off feature's own label
    // (shared/protocols/feature-flags.ts's flagDeniedBody), so the refusal
    // can still be said in words: "Create account is switched off right now."
    if (body && body.error === "feature_disabled") {
      return `${body.feature || "That"} is switched off right now.`;
    }
  } catch {
    // an empty or non-JSON body — the fallback speaks
  }
  return fallback;
}

/**
 * Ask who this browser is and seed the store. Best-effort like every golem
 * call: an unreachable platform means "signed out" for now, never a broken
 * app. Called once at boot (main.js).
 */
export async function initAccount(store) {
  try {
    const res = await fetch(`${API}/me`);
    if (!res.ok) return;
    const { user } = await res.json();
    store.commit("session/setAccount", slim(user));
  } catch {
    // no platform in reach — the app runs signed out, as it always could
  }
}

/**
 * FT-1226: LABS. Ask the platform which feature flags are on FOR THIS CALLER
 * (`GET /api/flags/self` — evaluated verdicts, booleans only; the cookie makes
 * an excepted account read true while the flag is off for everyone else) and
 * keep the one this app acts on: `labs`, the door to unfinished surfaces —
 * currently the guide and nothing else. Best-effort like the /me ask above:
 * any failure means labs stays false and the guide stays hidden, which is the
 * flag's own default. Called once at boot (main.js), beside initAccount.
 */
export async function initFlags(store) {
  try {
    const res = await fetch("/api/flags/self");
    if (!res.ok) return;
    const { flags } = await res.json();
    // FT-1237: not committed directly any more — the switch computes the
    // effective verdict (a standing local override wins) and commits the
    // same session/setLabs every reader has always watched.
    setServerLabs(store, !!(flags && flags.labs));
  } catch {
    // no platform in reach — labs off, exactly what the flag ships as
  }
}

/** Sign in. Resolves to the account; throws Error(server's message). */
export async function login(store, email, password) {
  const res = await fetch(`${API}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    throw new Error(await messageOf(res, "Couldn't sign in — try again."));
  }
  const { user } = await res.json();
  const account = slim(user);
  store.commit("session/setAccount", account);
  return account;
}

/**
 * Create an account (auto-signs-in on success). `name` is optional — it
 * becomes the table name the seats prefill with. Throws Error(server's
 * message): a taken email's 409, the flag refusal, a validation 400.
 */
export async function signup(store, email, password, name) {
  const body = { email, password };
  const trimmed = (name || "").trim();
  if (trimmed) body.name = trimmed;
  const res = await fetch(`${API}/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(
      await messageOf(res, "Couldn't create the account — try again."),
    );
  }
  const { user } = await res.json();
  const account = slim(user);
  store.commit("session/setAccount", account);
  return account;
}

/**
 * FT-1306: ASK FOR A RESET LINK. The platform answers 200 for every
 * well-formed email, existing or not, so neither this call nor anything the
 * panel says with it can be used to ask whether an address has an account.
 * The link that arrives by mail opens the PLATFORM's own reset page — the
 * app's part ends the moment the ask is accepted, which is why nothing is
 * committed to the store and no account argument is taken.
 *
 * Throws Error(server's message) on the rate-limited 429 or a malformed 400;
 * resolves (silently) on the 200.
 */
export async function forgotPassword(email) {
  const res = await fetch(`${API}/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw new Error(
      await messageOf(res, "Couldn't send the reset link — try again."),
    );
  }
}

/** Sign out. The cookie dies server-side; the store forgets either way. */
export async function logout(store) {
  try {
    await fetch(`${API}/logout`, { method: "POST" });
  } catch {
    // unreachable server — the local fact still clears; the cookie will be
    // refused or expired whenever the platform is next in reach
  }
  store.commit("session/setAccount", null);
}
